import { css } from "@emotion/css";
import { Button, Callout, ExternalLink } from "@octopusdeploy/design-system-components";
import type { PageAction, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { InfoCircleIcon } from "@octopusdeploy/design-system-icons";
import { fontSize, lineHeight, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useMutation, useQuery } from "@octopusdeploy/octopus-react-client";
import type { Repository, SigningKeyConfigurationResource, SigningKeyResource } from "@octopusdeploy/octopus-server-client";
import { compact } from "lodash";
import moment from "moment";
import { useState } from "react";
import { repository } from "~/clientInstance";
import ConfirmationDialog from "~/components/Dialog/ConfirmationDialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import OpenDialogButton from "~/components/Dialog/OpenDialogButton";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import { OverflowMenu, OverflowMenuItems } from "~/components/OverflowMenu/OverflowMenu";
import { PageContent } from "~/components/PageContent/PageContent";
import SimpleDataTable from "~/components/SimpleDataTable";
import { Note } from "~/components/form";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import DateFormatter from "~/utils/DateFormatter";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
import { EditSigningKeyConfiguration } from "./EditSigningKeyConfiguration";
export async function signingKeysPageLoader(repository: Repository): Promise<LoaderData> {
    const configuration = repository.SigningKeyConfiguration.get();
    const signingKeys = repository.SigningKeys.all();
    return {
        configuration: await configuration,
        signingKeys: await signingKeys,
    };
}
export const styles = {
    title: css({
        display: "flex",
        alignItems: "center",
        font: text.heading.small,
        fontSize: fontSize.medium,
        lineHeight: lineHeight.xSmall,
        marginTop: space[16],
    }),
    icon: css({
        marginRight: space[4],
    }),
};
interface LoaderData {
    configuration: SigningKeyConfigurationResource;
    signingKeys: SigningKeyResource[];
}
interface SigningKeysPageProps {
    loaderData: LoaderData;
}
export function SigningKeysPage({ loaderData }: SigningKeysPageProps) {
    const { isOpen: isConfirmRotateDialogOpen, closeAllDialogs: closeConfirmRotateDialog, openDialog: openConfirmRotateDialog } = useDialogTrigger();
    const [configuration, setConfiguration] = useState<SigningKeyConfigurationResource>(loaderData.configuration);
    const { result: signingKeys, refetch: refetchSigningKeys } = useQuery((repository) => repository.SigningKeys.all(), [], "Signing Keys", { initialResult: loaderData.signingKeys });
    const startRotateExternalSigningKeyWorkflow = useWorkflow(workflows.rotateExternalSigningKeyWorkflow);
    const { execute: rotateSigningKey, isExecuting: isRotatingSigningKey } = useMutation({
        name: "Rotate Signing Key",
        action: (repository) => repository.SigningKeys.rotate(),
        afterComplete: async () => refetchSigningKeys(),
    });
    const { execute: revokeSigningKey, error: revokeError } = useMutation({
        name: "Revoke Signing Key",
        action: (repository: Repository, signingKey: SigningKeyResource) => repository.SigningKeys.revoke(signingKey.Id),
        afterComplete: async () => refetchSigningKeys(),
    });
    const handleRotateClick = () => {
        if (configuration.PublicKeyHostingLocation === "External") {
            startRotateExternalSigningKeyWorkflow({ refetchSigningKeys }, () => {
                refetchSigningKeys();
            });
        }
        else {
            openConfirmRotateDialog();
        }
    };
    const primaryAction: PrimaryPageAction = {
        type: "button",
        disabled: isRotatingSigningKey,
        label: "Rotate Signing Key",
        onClick: handleRotateClick,
    };
    const exportSigningKeysAction: PageAction = {
        type: "navigate",
        buttonType: "secondary",
        label: "Export Signing Keys",
        path: repository.SigningKeys.getExportLink(),
        external: true,
    };
    const pageActions = configuration.PublicKeyHostingLocation === "External" ? [exportSigningKeysAction] : [];
    return (<PageContent header={{ title: signingKeysPageTitle, primaryAction, pageActions }} sidebar={<SigningKeysSidebar configuration={configuration} setConfiguration={setConfiguration}/>}>
            <ConfirmRotationDialog isOpen={isConfirmRotateDialogOpen} onConfirmRotate={rotateSigningKey} onClose={closeConfirmRotateDialog}/>
            <SigningKeyTable data={signingKeys} headerColumns={["State", "Id", "Created", "Expired", ""]} onRow={(key) => renderRow(key, signingKeys, configuration, revokeSigningKey, revokeError)}/>
        </PageContent>);
}
class SigningKeyTable extends SimpleDataTable<SigningKeyResource> {
}
function getExpiredColumnValue(signingKey: SigningKeyResource, signingKeys: SigningKeyResource[], configuration: SigningKeyConfigurationResource) {
    if (signingKey.Expired) {
        return DateFormatter.dateToShortFormat(signingKey.Expired);
    }
    if (configuration.PublicKeyHostingLocation === "Internal") {
        return <i>(Rotating on {calculateNextRotation(signingKeys, configuration)})</i>;
    }
    return null;
}
function renderRow(signingKey: SigningKeyResource, signingKeys: SigningKeyResource[], configuration: SigningKeyConfigurationResource, revoke: (signingKey: SigningKeyResource) => Promise<void>, revokeError: Error | null) {
    const signingKeyStatus = signingKey.Expired ? "Expired" : signingKey.Activated ? "Active" : "Pending";
    return [signingKeyStatus, signingKey.Id, DateFormatter.dateToShortFormat(signingKey.Created), getExpiredColumnValue(signingKey, signingKeys, configuration), getOverflowMenuItems(signingKey, revoke, revokeError)];
}
function calculateNextRotation(signingKeys: SigningKeyResource[], configuration: SigningKeyConfigurationResource) {
    const activeKey = signingKeys.find((k) => !k.Expired);
    if (!activeKey) {
        return null;
    }
    const rotationMoment = moment(activeKey.Created);
    rotationMoment.add(configuration.ExpireAfterDays, "d");
    return DateFormatter.dateToShortFormat(rotationMoment);
}
function SigningKeysSidebar({ setConfiguration, configuration }: {
    configuration: SigningKeyConfigurationResource;
    setConfiguration: (configuration: SigningKeyConfigurationResource) => void;
}) {
    const isSelfHostedEnabled = useOctopusFeatureToggle("github-app-for-self-hosted-octopus-instances", false);
    const startEditSigningKeyConfigurationWorkflow = useWorkflow(workflows.editSigningKeyConfigurationWorkflow);
    const handleEditClick = () => {
        startEditSigningKeyConfigurationWorkflow((updatedConfiguration) => {
            setConfiguration(updatedConfiguration);
        });
    };
    if (!isSelfHostedEnabled) {
        return (<div>
                <h4>Configuration</h4>
                <p>
                    The active signing key will be rotated every <b>{toDaysString(configuration.ExpireAfterDays)}</b>.
                </p>
                <p>
                    Expired keys will be revoked <b>{toDaysString(configuration.RevokeAfterDays)}</b> after they expire.
                    <Note>Until then they will continue to validate incoming requests.</Note>
                </p>
                <OpenDialogButton label="Change">
                    <EditSigningKeyConfiguration onSaveDone={(configuration) => setConfiguration(configuration)}/>
                </OpenDialogButton>
            </div>);
    }
    return (<div>
            <h4>Configuration</h4>
            {getSidebarText(configuration)}
            <Button label="Change" importance="secondary" onClick={handleEditClick}/>
        </div>);
}
function getSidebarText(configuration: SigningKeyConfigurationResource) {
    if (configuration.PublicKeyHostingLocation === "External") {
        return (<>
                <div className={styles.title}>
                    <span className={styles.icon}>
                        <InfoCircleIcon size={20} color={themeTokens.color.icon.info}/>
                    </span>
                    Externally hosted public keys
                </div>
                <p>Externally hosting public keys means you'll manage signing key rotation manually. To use keys in network-restricted environments, you'll need to export them.</p>
                <ExternalLink href="ExternalKeyRotation"> Learn more</ExternalLink>
                <p />
            </>);
    }
    return (<>
            <div className={styles.title}>
                <span className={styles.icon}>
                    <InfoCircleIcon size={20} color={themeTokens.color.icon.info}/>
                </span>
                Internally hosted public keys
            </div>
            <p>Internally hosting public keys means Octopus will manage signing key rotation automatically.</p>
            <ExternalLink href="InternalKeyRotation"> Learn more</ExternalLink>
            <p>
                The active signing key will be rotated every <b>{toDaysString(configuration.ExpireAfterDays)}</b>.
            </p>
            <p>
                Expired keys will be revoked <b>{toDaysString(configuration.RevokeAfterDays)}</b> after they expire.
                <Note>Until then they will continue to validate incoming requests.</Note>
            </p>
        </>);
}
function getOverflowMenuItems(signingKey: SigningKeyResource, revokeSigningKey: (signingKey: SigningKeyResource) => Promise<void>, revokeError: null | Error) {
    if (!signingKey.Expired) {
        return;
    }
    const revoke = OverflowMenuItems.dialogItem("Revoke", <OkDialogLayout title={"Revoke Signing Key"} errors={revokeError === null ? null : [revokeError]} okButtonLabel="Revoke" onOkClick={() => revokeSigningKey(signingKey)}>
            <p>Are you sure you would like to revoke this signing key?</p>
            <Callout title={"Revoking a signing key is permanent. There is no going back."} type={"warning"}/>
        </OkDialogLayout>);
    return <OverflowMenu menuItems={compact([revoke])}/>;
}
function toDaysString(days: number) {
    return `${days} ${days > 1 ? "days" : "day"}`;
}
interface ConfirmRotationDialogProps {
    isOpen: boolean;
    onConfirmRotate: () => Promise<void>;
    onClose: () => void;
}
function ConfirmRotationDialog({ isOpen, onConfirmRotate, onClose }: ConfirmRotationDialogProps) {
    return (<ConfirmationDialog title="Rotate Signing Key" continueButtonLabel="Rotate" open={isOpen} onClose={onClose} onContinueClick={onConfirmRotate}>
            <p>Are you sure you want to continue?</p>
            <Callout title="This will expire the active signing key and create a new one" type={"information"}>
                <p>Expired signing keys will continue to validate incoming requests until revoked.</p>
            </Callout>
        </ConfirmationDialog>);
}
export const signingKeysPageTitle = "Signing Keys";
